// src/pages/HostDashboard.jsx
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiPower } from 'react-icons/fi';

export default function HostDashboard() {
    const { user, token, logout, setUser, setToken } = useAuth();
    const navigate = useNavigate();

    // Profile
    const [editMode, setEditMode] = useState(false);
    const [profile, setProfile] = useState({
        name: user?.name || '',
        email: user?.email || ''
    });

    // Properties
    const [properties, setProperties] = useState([]);
    const [editPropertyId, setEditPropertyId] = useState(null);
    const [editPropertyData, setEditPropertyData] = useState({
        title: '',
        price: '',
        location: '',
        description: ''
    });
    const [title, setTitle] = useState('');
    const [price, setPrice] = useState('');
    const [location, setLocation] = useState('');

    // Reservations
    const [reservations, setReservations] = useState([]);

    // Reviews
    const [reviews, setReviews] = useState([]);

    // Loading
    const [loading, setLoading] = useState(true);

    // Load profile from user
    useEffect(() => {
        if (user) {
            setProfile({
                name: user.name || '',
                email: user.email || ''
            });
        }
    }, [user]);

    // Load properties, reservations, reviews on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!token) return;

                const [resProps, resReservations, resReviews] = await Promise.all([
                    axios.get('/api/host/properties', {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get('/api/host/reservations', {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get('/api/host/reviews', {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                setProperties(resProps.data.properties || []);
                setReservations(resReservations.data.reservations || []);
                setReviews(resReviews.data.reviews || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [token]);

    // Update profile
    const handleSaveProfile = async () => {
        try {
            const res = await axios.put('/api/host/profile', profile, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.token) {
                localStorage.setItem('token', res.data.token);
                setToken(res.data.token);
            }

            localStorage.setItem('user', JSON.stringify(res.data.user));
            setUser(res.data.user);

            setEditMode(false);
            alert('Profil actualizat cu succes!');
        } catch (err) {
            console.error(err);
            alert('Eroare la salvarea profilului');
        }
    };

    // Create property
    const handleCreateProperty = async (e) => {
        e.preventDefault();
        if (properties.length >= 10) {
            alert('Poti adauga maxim 10 proprietati');
            return;
        }
        try {
            const payload = {
                title,
                propertyType: location,
                pricePerNight: price,
            };

            // Adaugam clasificarea doar daca e Pensiune sau Hotel
            if (location === 'Pensiune' || location === 'Hotel') {
                // Foloseste editPropertyData.classification pentru a evita eroarea
                if (editPropertyData.classification !== undefined && editPropertyData.classification !== '') {
                    payload.classification = parseInt(editPropertyData.classification) || 0;
                } else {
                    payload.classification = 0; // valoare implicita
                }
            }

            console.log('FRONT → payload create property:', payload)

            await axios.post('/api/host/properties', payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Refetch proprietati
            setLoading(true);
            const res = await axios.get('/api/host/properties', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProperties(res.data.properties || res.data || []);

            // Reset form
            setTitle('');
            setLocation('');
            setEditPropertyData({ classification: '' });

            alert('Proprietate creata cu succes!');
        } catch (err) {
            console.error(err);
            alert('Eroare la crearea proprietatii');
        } finally {
            setLoading(false);
        }
    };

    // Edit property
    const handleEditProperty = (property) => {
        navigate(`/property/${property._id}`, { state: { editable: true } });
    };

    const handleSavePropertyEdit = async (propertyId) => {
        try {
            const res = await axios.put(`/api/host/properties/${propertyId}`, {
                title: editPropertyData.title,
                location: editPropertyData.location,
                description: editPropertyData.description
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setProperties(properties.map(p =>
                p._id === propertyId ? res.data.property : p
            ));
            setEditPropertyId(null);
            alert('Proprietate actualizata cu succes!');
        } catch (err) {
            console.error(err);
            alert('Eroare la actualizarea proprietatii');
        }
    };

    const handleCancelEdit = () => {
        setEditPropertyId(null);
        setEditPropertyData({
            title: '',
            price: '',
            location: '',
            description: ''
        });
    };

    const handleNavigateToPayments = () => {
        navigate('/payment');
    };

    if (loading) return (
        <div style={{
            padding: '2rem',
            textAlign: 'center',
            fontFamily: "'Sora', sans-serif",
            color: '#006d77'
        }}>
            Se incarca...
        </div>
    );

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleDeleteProperty = async (propertyId) => {
        if (!window.confirm('Sigur vrei sa stergi aceasta proprietate?')) return;

        try {
            await axios.delete(`/api/host/properties/${propertyId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setProperties(properties.filter(p => p._id !== propertyId));
            alert('Proprietate stearsa cu succes!');
        } catch (err) {
            console.error(err);
            alert('Eroare la stergerea proprietatii');
        }
    };

    return (
        <div style={{
            padding: '0',
            fontFamily: "'Sora', sans-serif",
            color: '#000',
            minHeight: '100vh',
            backgroundColor: '#fbfcf8'
        }}>
            {/* Header */}
            <div style={{
                padding: '0.3rem',
                borderBottom: '1px solid #dd4124',
                position: 'sticky',
                top: 0,
                backgroundColor: '#fff',
                zIndex: 100
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            color: '#dd4124',
                            cursor: 'pointer',
                            padding: '0.25rem'
                        }}
                        aria-label="Home"
                    >
                        ⇐
                    </button>
                    <h1 style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        color: '#000',
                        margin: 0
                    }}>
                        Panou Gazda
                    </h1>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={handleNavigateToPayments}
                            style={{
                                padding: '0.4rem 0.8rem',
                                backgroundColor: '#006d77',
                                color: 'white',
                                border: 'none',
                                borderRadius: '20px',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                cursor: 'pointer',
                                visibility: 'hidden'
                            }}
                        >
                            Plateste
                        </button>
                        <button
                            onClick={handleLogout}
                            style={{
                                width: '40px',
                                height: '40px',
                                minWidth: '40px',
                                minHeight: '40px',
                                backgroundColor: 'transparent',
                                color: '#dd4124',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                flexShrink: 0,
                            }}
                            aria-label="Logout"
                        >
                            <FiPower size={20} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div style={{
                padding: '1rem',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                {/* Profile Section */}
                <section style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem'
                    }}>
                        <h2 style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#000',
                            margin: 0
                        }}>
                            Profil
                        </h2>
                        {!editMode && (
                            <button
                                onClick={() => setEditMode(true)}
                                style={{
                                    padding: '0.2rem 0.6rem',
                                    fontSize: '0.85rem',
                                    border: '1px solid #006d77',
                                    backgroundColor: 'transparent',
                                    color: '#006d77',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Editeaza
                            </button>
                        )}
                    </div>

                    <div style={{
                        padding: '1rem',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px'
                    }}>
                        {editMode ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                <input
                                    value={profile.name}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                    placeholder="Nume"
                                    style={{
                                        padding: '0.6rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                <input
                                    value={profile.email}
                                    disabled
                                    style={{
                                        padding: '0.6rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        backgroundColor: '#f9f9f9',
                                        color: '#666',
                                        fontSize: '0.95rem'
                                    }}
                                />
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        onClick={handleSaveProfile}
                                        style={{
                                            flex: 1,
                                            padding: '0.6rem',
                                            backgroundColor: '#006d77',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Salveaza
                                    </button>
                                    <button
                                        onClick={() => setEditMode(false)}
                                        style={{
                                            flex: 1,
                                            padding: '0.6rem',
                                            backgroundColor: '#dd4124',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Anuleaza
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginBottom: '0.5rem'
                                }}>
                                    <span style={{ color: '#006d77' }}>👤</span>
                                    <span style={{ fontWeight: '500' }}>{user?.name || 'Nedefinit'}</span>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#666'
                                }}>
                                    <span>📧</span>
                                    <span>{user?.email}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Stats */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '0.75rem',
                    marginBottom: '1.5rem'
                }}>
                    <div style={{
                        padding: '1rem',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#006d77',
                            marginBottom: '0.25rem'
                        }}>
                            {properties.length}
                        </div>
                        <div style={{
                            fontSize: '0.8rem',
                            color: '#000',
                            fontWeight: '500'
                        }}>
                            Proprietati
                        </div>
                    </div>
                    <div style={{
                        padding: '1rem',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#006d77',
                            marginBottom: '0.25rem'
                        }}>
                            {reservations.length}
                        </div>
                        <div style={{
                            fontSize: '0.8rem',
                            color: '#000',
                            fontWeight: '500'
                        }}>
                            Rezervari
                        </div>
                    </div>
                    <div style={{
                        padding: '1rem',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#006d77',
                            marginBottom: '0.25rem'
                        }}>
                            {reviews.length}
                        </div>
                        <div style={{
                            fontSize: '0.8rem',
                            color: '#000',
                            fontWeight: '500'
                        }}>
                            Recenzii
                        </div>
                    </div>
                </div>

                {/* Properties Section */}
                <section style={{ marginBottom: '1.5rem' }}>
                    <h2 style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#000',
                        margin: '0 0 1rem 0'
                    }}>
                        Proprietati ({properties.length}/10)
                    </h2>

                    {/* Add Property Form */}
                    <form onSubmit={handleCreateProperty} style={{
                        marginBottom: '1rem',
                        padding: '1rem',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr',
                            gap: '0.8rem'
                        }}>
                            {/* Titlu proprietate */}
                            <input
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="Titlu proprietate"
                                required
                                style={{
                                    padding: '0.6rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem'
                                }}
                            />

                            {/* Preț pe noapte */}
                            <input
                                type="number"
                                value={price}
                                onChange={e => setPrice(e.target.value)}
                                placeholder="Pret pe noapte (RON)"
                                required
                                style={{
                                    padding: '0.6rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem'
                                }}
                            />

                            {/* Tip proprietate */}
                            <select
                                value={location} // aici pasam tipul proprietatii
                                onChange={e => setLocation(e.target.value)}
                                required
                                style={{
                                    padding: '0.6rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <option value="">Selecteaza tip proprietate</option>
                                {[
                                    'Pensiune',
                                    'Ferma',
                                    'Cabana',
                                    'Hotel',
                                    'Motel',
                                    'Hostel',
                                    'Han',
                                    'Vila',
                                    'Guest House',
                                    'Camping',
                                    'Glamping',
                                    'Rulote'
                                ].map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>

                            {/* Clasificare (doar pentru Pensiune si Hotel) */}
                            {(location === 'Pensiune' || location === 'Hotel') && (
                                <select
                                    value={editPropertyData.classification ?? ''}
                                    onChange={e =>
                                        setEditPropertyData({
                                            ...editPropertyData,
                                            classification: Number(e.target.value)
                                        })
                                    }
                                    required
                                    style={{
                                        padding: '0.6rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    <option value="">Clasificare</option>

                                    {location === 'Pensiune' && (
                                        <>
                                            <option value="0">—</option>
                                            <option value="1">🏵</option>
                                            <option value="2">🏵🏵</option>
                                            <option value="3">🏵🏵🏵</option>
                                            <option value="4">🏵🏵🏵🏵</option>
                                            <option value="5">🏵🏵🏵🏵🏵</option>
                                        </>
                                    )}

                                    {location === 'Hotel' && (
                                        <>
                                            <option value="1">★</option>
                                            <option value="2">★★</option>
                                            <option value="3">★★★</option>
                                            <option value="4">★★★★</option>
                                            <option value="5">★★★★★</option>
                                        </>
                                    )}
                                </select>
                            )}

                            {/* Submit */}
                            <button
                                type="submit"
                                style={{
                                    padding: '0.6rem',
                                    backgroundColor: '#006d77',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}
                            >
                                Adauga proprietate
                            </button>
                        </div>
                    </form>


                    {/* Properties List */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {properties.map(p => (
                            <div
                                key={p._id}
                                style={{
                                    padding: '1rem',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '8px'
                                }}
                            >
                                {editPropertyId === p._id ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                        <input
                                            value={editPropertyData.title}
                                            onChange={e => setEditPropertyData({ ...editPropertyData, title: e.target.value })}
                                            placeholder="Titlu"
                                            style={{
                                                padding: '0.6rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <input
                                            value={editPropertyData.location}
                                            onChange={e => setEditPropertyData({ ...editPropertyData, location: e.target.value })}
                                            placeholder="Locatie"
                                            style={{
                                                padding: '0.6rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <input
                                            type="number"
                                            value={editPropertyData.price}
                                            onChange={e => setEditPropertyData({ ...editPropertyData, price: e.target.value })}
                                            placeholder="Pret"
                                            style={{
                                                padding: '0.6rem',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px'
                                            }}
                                        />
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button
                                                onClick={() => handleSavePropertyEdit(p._id)}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.5rem',
                                                    backgroundColor: '#006d77',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                Salveaza
                                            </button>
                                            <button
                                                onClick={handleCancelEdit}
                                                style={{
                                                    flex: 1,
                                                    padding: '0.5rem',
                                                    backgroundColor: '#dd4124',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                Anuleaza
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginBottom: '0.5rem'
                                        }}>
                                            <div>
                                                <h4 style={{
                                                    margin: '0 0 0.3rem 0',
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    color: '#000'
                                                }}>
                                                    {p.title}
                                                </h4>
                                                <div style={{
                                                    fontSize: '0.85rem',
                                                    color: '#666',
                                                    marginBottom: '0.3rem'
                                                }}>
                                                    📍 {p.location}
                                                </div>
                                                <div style={{
                                                    fontSize: '0.95rem',
                                                    fontWeight: '600',
                                                    color: '#006d77'
                                                }}>
                                                    {new Intl.NumberFormat('ro-RO').format(p.pricePerNight)} RON/noapte
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.3rem' }}>
                                                <button
                                                    onClick={() => handleEditProperty(p)}
                                                    style={{
                                                        padding: '0.3rem 0.6rem',
                                                        border: '1px solid #006d77',
                                                        backgroundColor: 'transparent',
                                                        color: '#006d77',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProperty(p._id)}
                                                    style={{
                                                        padding: '0.3rem 0.6rem',
                                                        border: '1px solid #dd4124',
                                                        backgroundColor: 'transparent',
                                                        color: '#dd4124',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '0.8rem'
                                                    }}
                                                >
                                                    Sterge
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* Reservations and Reviews - Stacked on mobile */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: '1rem'
                }}>
                    {/* Reservations */}
                    <section>
                        <h2 style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#000',
                            margin: '0 0 1rem 0'
                        }}>
                            Rezervari recente
                        </h2>
                        <div style={{
                            padding: '1rem',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px'
                        }}>
                            {reservations.length === 0 ? (
                                <p style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center' }}>
                                    Nu exista rezervari
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {reservations.slice(0, 3).map(r => (
                                        <div
                                            key={r._id}
                                            style={{
                                                padding: '0.75rem',
                                                borderBottom: '1px solid #f0f0f0'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                color: '#000',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {r.propertyTitle}
                                            </div>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: '#666',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {r.startDate} → {r.endDate}
                                            </div>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '0.2rem 0.6rem',
                                                backgroundColor: r.status === 'confirmed' ? '#e6f7f0' : '#fff3e6',
                                                color: r.status === 'confirmed' ? '#006d77' : '#dd4124',
                                                borderRadius: '12px',
                                                fontSize: '0.75rem',
                                                fontWeight: '500'
                                            }}>
                                                {r.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Reviews */}
                    <section>
                        <h2 style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: '#000',
                            margin: '0 0 1rem 0'
                        }}>
                            Recenzii recente
                        </h2>
                        <div style={{
                            padding: '1rem',
                            border: '1px solid #f0f0f0',
                            borderRadius: '8px'
                        }}>
                            {reviews.length === 0 ? (
                                <p style={{ color: '#666', fontSize: '0.9rem', textAlign: 'center' }}>
                                    Nu exista recenzii
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {reviews.slice(0, 3).map(r => (
                                        <div
                                            key={r._id}
                                            style={{
                                                padding: '0.75rem',
                                                borderBottom: '1px solid #f0f0f0'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                color: '#000',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {r.propertyTitle}
                                            </div>
                                            <p style={{
                                                margin: '0.25rem 0',
                                                fontSize: '0.85rem',
                                                color: '#666',
                                                lineHeight: '1.4'
                                            }}>
                                                "{r.text}"
                                            </p>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: '#006d77',
                                                fontWeight: '500'
                                            }}>
                                                ⭐ {r.rating}/5
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </div>

            {/* Media Queries for Larger Screens */}
            <style>
                {`
                @media (min-width: 768px) {
                    div[style*="gridTemplateColumns: '1fr'"]:last-of-type {
                        grid-template-columns: 1fr 1fr;
                    }
                    form > div[style*="gridTemplateColumns: '1fr'"] {
                        grid-template-columns: 1fr 1fr 1fr auto;
                    }
                }
                `}
            </style>
        </div>
    );
}